import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, redirect } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useEffect } from "react";

import { LEDDot } from "@/components/ui/LEDDot";
import { api, type AgentGridCard, type AgentRunStatus } from "@/lib/api";
import { cn, formatRelativeTime } from "@/lib/formatters";
import { isAuthenticated, pb } from "@/lib/pb";

export const Route = createFileRoute("/agents/")({
  beforeLoad: () => {
    if (!isAuthenticated()) throw redirect({ to: "/login" });
  },
  component: AgentsGrid,
});

const STATUS_LED: Record<AgentRunStatus, "ok" | "warn" | "danger" | "info" | "off"> = {
  queued: "warn",
  running: "info",
  success: "ok",
  failure: "danger",
  cancelled: "off",
};

function AgentsGrid() {
  const qc = useQueryClient();

  const gridQuery = useQuery({
    queryKey: ["agents-grid"],
    queryFn: () => api.agents.grid(),
    refetchInterval: (query) =>
      (query.state.data?.cards ?? []).some((c) => c.active > 0) ? 15_000 : 60_000,
  });

  // agent_runs is written by both pollers; a run that finishes while the tab is
  // open should move the card without waiting for the next refetch.
  useEffect(() => {
    const unsub = pb
      .collection("agent_runs")
      .subscribe("*", () => qc.invalidateQueries({ queryKey: ["agents-grid"] }))
      .catch(() => () => {});
    return () => {
      Promise.resolve(unsub).then((fn) => typeof fn === "function" && fn());
    };
  }, [qc]);

  const cards = gridQuery.data?.cards ?? [];
  const runningTotal = cards.reduce((n, c) => n + c.active, 0);

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="path-chip">/agents</p>
          <h1 className="text-3xl font-semibold mt-1">Agents</h1>
        </div>
        <p className="font-mono text-xs text-muted">
          {cards.length} agentes · {runningTotal} corriendo
        </p>
      </div>

      {gridQuery.error && (
        <div className="surface px-4 py-3 font-mono text-sm text-danger">
          {(gridQuery.error as Error).message}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {cards.map((card, i) => (
          <motion.div
            key={card.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(i * 0.05, 0.3) }}
          >
            <AgentCard card={card} />
          </motion.div>
        ))}

        {gridQuery.isLoading && cards.length === 0 && (
          <div className="surface p-6 font-mono text-sm text-muted">$ cargando agentes…</div>
        )}
      </div>
    </div>
  );
}

function AgentCard({ card }: { card: AgentGridCard }) {
  const latest = card.latest;
  const led = latest?.status ? STATUS_LED[latest.status] : "off";

  return (
    <Link
      to={card.detail_path}
      className="surface block p-5 h-full transition-all hover:border-primary/40 hover:shadow-[0_0_24px_rgba(0,212,255,0.10)]"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">{card.name}</h2>
          <p className="path-chip mt-1">{card.detail_path}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0 font-mono text-[11px] text-muted">
          {card.active > 0 && (
            <span className="text-info">{card.active} corriendo</span>
          )}
          <LEDDot state={card.active > 0 ? "info" : led} size={7} pulse={card.active > 0} />
        </div>
      </div>

      <p className="mt-3 text-sm text-muted/90 leading-relaxed">{card.does}</p>

      <div className="mt-4 pt-3 border-t border-border/40">
        {!card.available ? (
          <p className="font-mono text-xs text-danger">no disponible · {card.error}</p>
        ) : latest ? (
          <div className="space-y-1">
            <div className="flex items-center gap-2 font-mono text-[11px] text-muted">
              <span
                className={cn(
                  "px-1.5 py-0.5 rounded-sm border uppercase",
                  latest.status === "failure"
                    ? "text-danger border-danger/40"
                    : latest.status === "success"
                      ? "text-success border-success/40"
                      : "text-muted border-border",
                )}
              >
                {latest.status}
              </span>
              {latest.mode && <span>{latest.mode}</span>}
              {latest.agent_status && <span className="text-muted/70">· {latest.agent_status}</span>}
              <span className="ml-auto">
                {latest.started_at ? formatRelativeTime(latest.started_at) : "—"}
              </span>
            </div>
            {latest.summary && (
              <p className="text-xs text-muted/80 leading-snug line-clamp-2">{latest.summary}</p>
            )}
          </div>
        ) : (
          <p className="font-mono text-xs text-muted/60">sin corridas todavía</p>
        )}

        <div className="mt-2 flex items-center gap-3 font-mono text-[10px] text-muted/60">
          <span>{card.recent_runs > 0 ? `últimas ${card.recent_runs}` : "sin corridas"}</span>
          {card.cost_usd != null && <span>${card.cost_usd.toFixed(2)}</span>}
          {!card.launchable && <span className="ml-auto">solo lectura</span>}
        </div>
      </div>
    </Link>
  );
}
